import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Calendar, Layers, Hash, FileText, ArrowRight, X, Filter, CheckSquare, StickyNote, Sparkles } from "lucide-react";
import { api, NODE_TYPES, MOD_KEY } from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { useClasses } from "../context/ClassesContext";
import { useTemplates } from "../context/TemplatesContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * Inline sidebar search — replaces the Cmd+K Spotlight modal.
 *
 * Renders as a persistent search bar pinned to the top of the Sidebar. Clicking
 * the input opens a dropdown with fuzzy-matched results across workspaces,
 * classes, templates, nodes (events) + a few built-in actions.
 *
 * Filter menu (multi-select): a node-type section at the top (task, routine,
 * trigger, action, reminder, condition, sequence, note) and a divider with
 * "extras" below (has subtasks, has notes). Multiple filters can be combined;
 * any active filter restricts results to node (event) hits.
 *
 * Also hosts a Natural Language "+ create" affordance — type what you want and
 * press Cmd/Ctrl+Enter to ask the LLM to materialise an event.
 */
function fuzzyScore(q, text) {
  if (!q) return 1;
  q = q.toLowerCase();
  const s = (text || "").toLowerCase();
  if (!s) return 0;
  if (s === q) return 1000;
  if (s.startsWith(q)) return 500;
  if (s.includes(q)) return 250;
  let i = 0, j = 0, hit = 0;
  while (i < q.length && j < s.length) {
    if (q[i] === s[j]) { hit += 1; i += 1; }
    j += 1;
  }
  return i === q.length ? 50 + hit : 0;
}

const TYPE_META = {
  event: { icon: Calendar, label: "Node" },
  workspace: { icon: Layers, label: "Workspace" },
  class: { icon: Hash, label: "Class" },
  template: { icon: FileText, label: "Template" },
  action: { icon: ArrowRight, label: "Action" },
};

export default function SidebarSearch({ inline = false }) {
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const [events, setEvents] = useState([]);
  const [focused, setFocused] = useState(false);
  // Multi-select filters — any active filter restricts results to events.
  const [typeFilter, setTypeFilter] = useState(() => new Set());   // e.g. {'task','routine'}
  const [extraFilter, setExtraFilter] = useState(() => new Set()); // {'subtasks','notes'}
  const [filterOpen, _setFilterOpen] = useState(false); // kept for hasActiveFilter below
  const [nlLoading, setNlLoading] = useState(false);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const rootRef = useRef(null);

  const { workspaces, current, setCurrent } = useWorkspace();
  const { classes, setActiveClassId } = useClasses();
  const { templates } = useTemplates();
  const { logout } = useAuth();

  // Load events for the active workspace whenever it changes.
  useEffect(() => {
    if (!current?.workspace_id) { setEvents([]); return; }
    api.get(`/workspaces/${current.workspace_id}/events`)
      .then(({ data }) => setEvents(data || []))
      .catch(() => setEvents([]));
  }, [current?.workspace_id]);

  // Refresh the events list after external mutations (e.g. NL create).
  useEffect(() => {
    const refresh = () => {
      if (!current?.workspace_id) return;
      api.get(`/workspaces/${current.workspace_id}/events`)
        .then(({ data }) => setEvents(data || []))
        .catch(() => {});
    };
    window.addEventListener("crescendo:events-changed", refresh);
    return () => window.removeEventListener("crescendo:events-changed", refresh);
  }, [current?.workspace_id]);

  // Close the dropdown when clicking outside.
  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) { setFocused(false); _setFilterOpen(false); }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const actions = useMemo(() => [
    { id: "a:new-ws", label: "New Workspace", subtitle: "Create A Workspace", run: () => window.dispatchEvent(new CustomEvent("crescendo:new-workspace")) },
    { id: "a:history", label: "Version History", subtitle: "Browse Saved Snapshots", run: () => window.dispatchEvent(new CustomEvent("crescendo:open-history")) },
    { id: "a:settings", label: "Open Settings", subtitle: "Theme, Account, Shortcuts", run: () => window.dispatchEvent(new CustomEvent("crescendo:open-settings")) },
    { id: "a:shortcuts", label: "Keyboard Shortcuts", subtitle: "Show The Legend", run: () => window.dispatchEvent(new CustomEvent("crescendo:open-legend")) },
    { id: "a:logout", label: "Sign Out", subtitle: "Log Out Of This Account", run: () => logout?.() },
  ], [logout]);

  const universe = useMemo(() => {
    const items = [];
    for (const w of workspaces || []) items.push({ kind: "workspace", id: w.workspace_id, title: w.name, subtitle: "Switch Workspace", color: w.color, data: w });
    for (const c of classes || []) items.push({ kind: "class", id: c.class_id, title: c.name, subtitle: c.scope === "global" ? "Global Class" : "Class", color: c.color, data: c });
    for (const t of templates || []) items.push({ kind: "template", id: t.template_id, title: t.name, subtitle: `Template · ${(t.config?.events || []).length} Node(s)`, data: t });
    for (const ev of events || []) items.push({
      kind: "event",
      id: ev.event_id,
      title: ev.title || `(untitled ${ev.type})`,
      subtitle: `${ev.type.toUpperCase()} · ${new Date(ev.start).toLocaleString()}`,
      eventType: ev.type,
      hasSubtasks: Array.isArray(ev.subtasks) && ev.subtasks.length > 0,
      hasNotes: Array.isArray(ev.children_event_ids) && (ev.children_event_ids || []).some((cid) => {
        const child = events.find((x) => x.event_id === cid);
        return child && child.type === "note";
      }),
      data: ev,
    });
    for (const a of actions) items.push({ kind: "action", id: a.id, title: a.label, subtitle: a.subtitle, data: a });
    return items;
  }, [workspaces, classes, templates, events, actions]);

  const hasActiveFilter = typeFilter.size > 0 || extraFilter.size > 0;

  const results = useMemo(() => {
    let pool = universe;
    // Any active filter implicitly narrows to events (nodes) only — the
    // user's mental model is "I'm filtering nodes".
    if (hasActiveFilter) {
      pool = pool.filter((i) => i.kind === "event");
      if (typeFilter.size > 0) pool = pool.filter((i) => typeFilter.has(i.eventType));
      if (extraFilter.has("subtasks")) pool = pool.filter((i) => i.hasSubtasks);
      if (extraFilter.has("notes")) pool = pool.filter((i) => i.hasNotes);
    }
    if (!query.trim()) return pool.slice(0, 20);
    return pool
      .map((item) => ({ item, score: Math.max(fuzzyScore(query, item.title), fuzzyScore(query, item.subtitle) * 0.3) }))
      .filter((s) => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 20)
      .map((s) => s.item);
  }, [query, universe, typeFilter, extraFilter, hasActiveFilter]);

  useEffect(() => { if (cursor >= results.length) setCursor(0); }, [results.length, cursor]);
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-search-index="${cursor}"]`);
    if (el?.scrollIntoView) el.scrollIntoView({ block: "nearest" });
  }, [cursor]);

  const run = (item) => {
    if (!item) return;
    setFocused(false);
    setQuery("");
    switch (item.kind) {
      case "workspace": setCurrent?.(item.data.workspace_id); break;
      case "class": setActiveClassId?.(item.data.class_id); break;
      case "template": window.dispatchEvent(new CustomEvent("crescendo:instantiate-template", { detail: item.data })); break;
      case "event": window.dispatchEvent(new CustomEvent("crescendo:focus-event", { detail: item.data })); break;
      case "action": item.data.run?.(); break;
      default: break;
    }
  };

  const toggle = (setter, key) => setter((prev) => {
    const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n;
  });

  const runAiSort = async () => {
    if (!current?.workspace_id || !query.trim()) return;
    setNlLoading(true);
    try {
      const { data } = await api.post(
        `/workspaces/${current.workspace_id}/events/ai-sort`,
        { instruction: query.trim() }
      );
      if (data?.sorted > 0) {
        toast.success(`AI sorted ${data.sorted} task${data.sorted === 1 ? "" : "s"}`);
        window.dispatchEvent(new CustomEvent("crescendo:events-changed"));
      } else {
        toast.info(data?.message || "Nothing to sort.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI sort failed");
    } finally {
      setNlLoading(false);
    }
  };

  const runNaturalLanguage = async () => {
    const text = query.trim();
    if (!text || !current?.workspace_id) return;
    setNlLoading(true);
    try {
      const _tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const _d = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const _offset = -_d.getTimezoneOffset();
      const _sign = _offset >= 0 ? "+" : "-";
      const _abs = Math.abs(_offset);
      const _localIso = `${_d.getFullYear()}-${pad(_d.getMonth() + 1)}-${pad(_d.getDate())}T${pad(_d.getHours())}:${pad(_d.getMinutes())}:${pad(_d.getSeconds())}${_sign}${pad(Math.floor(_abs / 60))}:${pad(_abs % 60)}`;

      // "find" / "?" prefix → natural language search over existing events
      const isSearch = text.startsWith("?") || /^(find|show|list|when|what|which)\b/i.test(text);
      if (isSearch) {
        const { data } = await api.post(`/workspaces/${current.workspace_id}/events/nl-search`, {
          query: text.replace(/^\?/, "").trim(),
          client_now_iso: _localIso,
          client_tz: _tz,
        });
        if (data?.results?.length > 0) {
          data.results.forEach(ev => window.dispatchEvent(new CustomEvent("crescendo:focus-event", { detail: ev })));
          toast.success(`Found ${data.results.length} matching task${data.results.length !== 1 ? "s" : ""}`);
        } else {
          toast.info("No matching tasks found");
        }
        setQuery(""); setFocused(false);
        return;
      }

      const { data } = await api.post(`/workspaces/${current.workspace_id}/events/from-text`, {
        text, client_now_iso: _localIso, client_tz: _tz,
      });
      toast.success(`Created "${data.title || data.type}"`, { className: "reminder-toast" });
      window.dispatchEvent(new CustomEvent("crescendo:events-changed"));
      setQuery("");
      setFocused(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create from text");
    } finally {
      setNlLoading(false);
    }
  };

  const showDropdown = focused && (query.trim().length > 0 || hasActiveFilter);

  return (
    <div ref={rootRef} className={`relative ${inline ? "" : "px-3 pt-3 pb-2 border-b border-[#2A2A2A]"}`} data-testid="sidebar-search-root">
      <div className="flex items-stretch gap-1.5">
        <div className={`flex-1 flex items-center gap-2 px-2 rounded-md border ${focused ? "border-[#3A3A3A] bg-[#121212]" : "border-[#2A2A2A] bg-[#0A0A0A]"} transition-colors`}>
          <Search className="w-3.5 h-3.5 text-[#A1A1AA] flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setCursor(0); }}
            onFocus={() => setFocused(true)}
            placeholder="Search or type to create with AI — ↵  ·  ? to find"
            className="flex-1 bg-transparent outline-none text-xs py-1.5 text-[#EDEDED] placeholder:text-[#555]"
            data-testid="sidebar-search-input"
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); runNaturalLanguage(); return; }
              if (e.key === "Escape") { e.preventDefault(); setFocused(false); setQuery(""); inputRef.current?.blur(); }
              if (e.key === "ArrowDown") { e.preventDefault(); setFocused(true); setCursor((c) => Math.min(results.length - 1, c + 1)); }
              if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
              if (e.key === "Enter") {
                e.preventDefault();
                // Plain Enter — if there are matching results, run the
                // highlighted one. Otherwise treat the input as a natural
                // language command and dispatch to the AI (user request:
                // "use Enter to send, not Cmd+Enter").
                if (results.length > 0) run(results[cursor]);
                else runNaturalLanguage();
              }
            }}
          />
          {query && (
            <button onClick={() => { setQuery(""); inputRef.current?.focus(); }}
              className="p-0.5 rounded hover:bg-[#2a2a2a] text-[#A1A1AA]" data-testid="sidebar-search-clear">
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {showDropdown && (
        <div
          ref={listRef}
          className="absolute left-3 right-3 top-full mt-1 z-30 bg-[#141414] border border-[#2A2A2A] rounded-md shadow-2xl max-h-[50vh] overflow-y-auto py-1"
          data-testid="sidebar-search-results"
        >
          {/* Natural-language create chip — shown when user has typed something. */}
          {query.trim().length > 0 && current?.workspace_id && (
            <button
              onMouseDown={(e) => { e.preventDefault(); runNaturalLanguage(); }}
              disabled={nlLoading}
              className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-left bg-[#0055FF]/10 hover:bg-[#0055FF]/25 border-b border-[#2A2A2A] text-[#EDEDED] disabled:opacity-60"
              data-testid="sidebar-search-nl-create"
            >
              <Sparkles className="w-3.5 h-3.5 text-[#5A90F5] flex-shrink-0" />
              <span className="truncate flex-1">{nlLoading ? "Creating…" : <>Create From <span className="italic text-[#A1A1AA]">"{query.trim()}"</span></>}</span>
              <kbd className="bg-[#0A0A0A] border border-[#2A2A2A] rounded px-1 py-0.5 text-[9px] text-[#A1A1AA]">↵</kbd>
            </button>
          )}
          {query.trim().length > 0 && current?.workspace_id && (
            <button
              onMouseDown={(e) => { e.preventDefault(); runAiSort(); }}
              disabled={nlLoading}
              className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs text-left bg-[#5A3FBF]/10 hover:bg-[#5A3FBF]/25 border-b border-[#2A2A2A] text-[#EDEDED] disabled:opacity-60"
              data-testid="sidebar-search-ai-sort"
            >
              <Sparkles className="w-3.5 h-3.5 text-[#A78BFA] flex-shrink-0" />
              <span className="truncate flex-1">{nlLoading ? "Sorting…" : <>Sort tasks: <span className="italic text-[#A1A1AA]">"{query.trim()}"</span></>}</span>
            </button>
          )}
          {results.length === 0 ? (
            <div className="px-3 py-4 text-center text-[11px] text-[#A1A1AA]">No Results</div>
          ) : (
            results.map((r, i) => {
              const Meta = TYPE_META[r.kind] || TYPE_META.action;
              const Icon = Meta.icon;
              const selected = i === cursor;
              return (
                <div
                  key={`${r.kind}-${r.id}`}
                  data-search-index={i}
                  onMouseEnter={() => setCursor(i)}
                  onMouseDown={(e) => { e.preventDefault(); run(r); }}
                  className={`flex items-center gap-2 px-2.5 py-1.5 cursor-pointer text-xs ${selected ? "bg-[#0055FF]/80 text-white" : "hover:bg-[#1a1a1a] text-[#EDEDED]"}`}
                  data-testid={`sidebar-search-result-${i}`}
                >
                  {r.color ? (
                    <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: r.color }} />
                  ) : (
                    <Icon className="w-3 h-3 flex-shrink-0 opacity-80" />
                  )}
                  <span className="truncate flex-1">{r.title}</span>
                  <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${selected ? "bg-white/15 text-white" : "bg-[#0A0A0A] text-[#A1A1AA]"}`}>
                    {r.kind === "event" ? (r.eventType || Meta.label) : Meta.label}
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
